import { type ReactNode } from 'react';
import { UNSTABLE_ToastQueue as AriaToastQueue } from 'react-aria-components';

export type ToastVariant = 'neutral' | 'onchain' | 'error';

export type ToastOptions = {
  /**
   * An optional action element to render within the toast. For "onchain"
   * toasts, this will be a link to the appropriate block explorer.
   */
  actionElement?: ReactNode | ((key: string) => ReactNode);
  /**
   * The duration, in milliseconds, to wait before automatically dismissing the
   * toast. Not appropriate for "error" variants.
   */
  duration?: number;
  /** The contents of the toast. */
  content: ReactNode;
  /** Provide a concise title for the toast. */
  title: string;
};

export type ToastValue = Omit<ToastOptions, 'duration'> & {
  /**
   * The variant of the toast.
   */
  variant: ToastVariant;
};

// There is a single global toast queue instance for the whole app, initialized lazily.
let globalToastQueue: AriaToastQueue<ToastValue> | null = null;
export function getGlobalToastQueue() {
  if (!globalToastQueue) {
    globalToastQueue = new AriaToastQueue<ToastValue>({
      // Visibility is managed with styles by the `Toaster` component. This lets
      // us animate between collapsed and expanded states.
      maxVisibleToasts: Infinity,
    });
  }

  return globalToastQueue;
}

/** @private For testing. */
export function clearAllToasts() {
  globalToastQueue = null;
}

/**
 * Toasts display brief, sometimes temporary notifications. They’re meant to be
 * noticed without disrupting a user’s experience.
 *
 * [Taurus docs: `ToastQueue`](https://ui-sb.app.infinex.dev/?path=/docs/components-toast--docs)
 */
export const ToastQueue = {
  /**
   * Add a toast to the queue.
   * @returns A key that can be used to remove the toast from the queue.
   */
  add(variant: ToastVariant, options: ToastOptions) {
    return addToast(variant, options);
  },
  /**
   * Toasts may be programmatically removed if they become irrelevant before the
   * user manually closes them.
   */
  remove(key: string) {
    const queue = getGlobalToastQueue();
    return queue.close(key);
  },
  /**
   * Remove all toasts from the queue.
   */
  removeAll() {
    const queue = getGlobalToastQueue();
    while (queue.visibleToasts.length > 0) {
      queue.close(queue.visibleToasts[0].key);
    }
  },
};

const MIN_DURATION = 5000;

/** Opinionated wrapper for the `add` method. */
function addToast(variant: ToastVariant, options: ToastOptions) {
  const { duration, ...props } = options;

  // clamp the timeout for accessibility
  const timeout = duration ? Math.max(duration, MIN_DURATION) : undefined;

  // runtime warnings
  if (duration && duration < MIN_DURATION) {
    console.warn(
      `The \`duration\` option must be at least ${MIN_DURATION}ms for accessibility, and has been clamped for this instance.`
    );
  }
  if (variant === 'error' && duration) {
    console.warn(
      `We recommend omitting the \`duration\` for "error" notifications. They are often critical and should not be auto-dismissed.`
    );
  }

  // convert args to appropriate props for the `Toast` component
  const value = { ...props, variant };
  const queue = getGlobalToastQueue();
  return queue.add(value, { timeout });
}
